import { FC, useMemo, useState } from 'react';
import { DropdownMenu, Button, Icon } from '@kontentino/ui';
import { useTranslation } from 'react-i18next';
import { faImageSlash } from '@fortawesome/pro-regular-svg-icons/faImageSlash';
import {
  useApprovedPosts,
  ApprovedPost,
} from '../context/ApprovedPostsContext';
import Popup from 'utils/popup';
import ApprovedPostDropdownItem from './approvedPostsDropdown/ApprovedPostDropdownItem';
import { ApprovalThreadType } from '../types';

const MAX_VISIBLE_THUMBNAILS = 5;

interface ApprovedPostsDropdownProps {
  activeTab: ApprovalThreadType;
}

const ApprovedPostsDropdown: FC<ApprovedPostsDropdownProps> = ({
  activeTab,
}) => {
  const { t } = useTranslation();
  const { approvedPosts, revertApproval, isRevertingApproval } =
    useApprovedPosts();
  const [isOpen, setIsOpen] = useState(false);

  const filteredApprovedPosts = useMemo(
    () => approvedPosts.filter((post) => post.thread === activeTab),
    [approvedPosts, activeTab],
  );

  if (filteredApprovedPosts.length === 0) {
    return null;
  }

  const handleRevertApproval = async (post: ApprovedPost) => {
    const confirmationMessage =
      post.thread === 'internal'
        ? t('revertApprovalConfirmation.internal', {
            defaultValue:
              'The post will change <strong>back</strong> to pending internal approval',
          })
        : t('revertApprovalConfirmation.client', {
            defaultValue:
              'The post will change <strong>back</strong> to pending client approval',
          });

    const { isConfirmed } = await Popup.confirm({
      html: confirmationMessage,
    });

    if (isConfirmed) {
      revertApproval(post.id);
    }
  };

  return (
    <div className="tw-max-w-[348px] tw-pb-4 tw-pt-8">
      <DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenu.Trigger asChild>
          <div
            className="tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-gap-4 tw-rounded-md tw-bg-grayscale-20 tw-px-4 tw-py-2 tw-transition-colors hover:tw-bg-grayscale-30"
            data-name="approvals-approved-posts-dropdown-trigger"
            data-cy="approvals-approved-posts-dropdown-trigger"
          >
            <div className="tw-flex tw-items-center tw-gap-4">
              <div className="tw-flex tw-items-center tw-pr-2">
                {filteredApprovedPosts
                  .slice(0, MAX_VISIBLE_THUMBNAILS)
                  .map((post, index) => (
                    <div
                      key={post.id}
                      className="tw-relative tw-flex tw-h-6 tw-w-6 tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-md tw-border tw-border-grayscale-10 tw-bg-grayscale-20"
                      style={{
                        marginRight:
                          index < MAX_VISIBLE_THUMBNAILS - 1 ? '-16px' : 0,
                        zIndex: MAX_VISIBLE_THUMBNAILS - index,
                      }}
                    >
                      {post.thumbnail ? (
                        <img
                          src={post.thumbnail}
                          alt=""
                          className="tw-h-full tw-w-full tw-object-cover"
                        />
                      ) : (
                        <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-bg-grayscale-20">
                          <Icon
                            icon={faImageSlash}
                            className="tw-text-grayscale-50"
                            size={12}
                          />
                        </div>
                      )}
                    </div>
                  ))}
              </div>
              <span className="tw-text-sm tw-font-medium tw-text-grayscale-180">
                {t('postsHaveBeenApproved', {
                  count: filteredApprovedPosts.length,
                  defaultValue: `${filteredApprovedPosts.length} posts have been approved`,
                })}
              </span>
            </div>
            <Button
              variant="ghost"
              size="small"
              data-name="approvals-approved-posts-see-button"
              data-cy="approvals-approved-posts-see-button"
              className="tw-h-5"
            >
              {t('see', { defaultValue: 'See' })}
            </Button>
          </div>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className="tw-z-30 tw-flex tw-max-h-[320px] tw-w-full tw-max-w-[248px] tw-flex-col tw-gap-1 tw-overflow-y-auto tw-rounded-lg tw-bg-white tw-p-2 tw-shadow-md"
            align="end"
            data-name="approvals-approved-posts-dropdown-content"
            data-cy="approvals-approved-posts-dropdown-content"
            sideOffset={4}
          >
            {filteredApprovedPosts.map((post) => (
              <ApprovedPostDropdownItem
                key={post.id}
                post={post}
                onRevertApproval={handleRevertApproval}
                closeDropdown={() => setIsOpen(false)}
                isReverting={isRevertingApproval(post.id)}
              />
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
};

export default ApprovedPostsDropdown;
